import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { MipSoilClassificationDTO, SoilClassificationOutputDTO, TruckRecordingMipService } from '../generated';
import { AttributeChipData } from '../../components/Prediction/types';
import { SOIL_CATEGORY_VALUES } from '../resources/soilClassification.constants';
import { transformSoilClassification } from './soilClassification';

export interface TruckRecordingMipSoilClassificationOptionsProps {
  jobsiteId?: string;
  mipReportId?: string;
  enabled?: boolean;
}

export function truckRecordingMipSoilClassificationOptions({
  jobsiteId,
  mipReportId,
  enabled = true,
}: TruckRecordingMipSoilClassificationOptionsProps) {
  return queryOptions<AttributeChipData[], ApiError>({
    queryKey: ['truckRecordingMipSoilClassification', jobsiteId, mipReportId],
    queryFn: async () => {
      if (!jobsiteId || !mipReportId) {
        throw new Error('jobsiteId and mipReportId are required');
      }

      const response = await TruckRecordingMipService.truckRecordingMipControllerGetSoilClassification({
        path: { jobsiteId, mipReportId },
      });

      if (response.data !== undefined) {
        return transformMipSoilClassification(response.data);
      }

      throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
    },
    enabled: !!jobsiteId && !!mipReportId && !!enabled,
  });
}

export function useTruckRecordingMipSoilClassification(props: TruckRecordingMipSoilClassificationOptionsProps) {
  return useQuery<AttributeChipData[], ApiError>(truckRecordingMipSoilClassificationOptions(props));
}

export function transformMipSoilClassification(resp: MipSoilClassificationDTO): AttributeChipData[] {
  const data = resp.data;

  const colors: SoilClassificationOutputDTO['color'] = {};
  for (const color of SOIL_CATEGORY_VALUES.color) {
    const colorKey = `color.${color}` as keyof MipSoilClassificationDTO['data'];
    const colorValue = data[colorKey];
    if (colorValue !== undefined) colors[color] = colorValue;
  }

  const brightness: SoilClassificationOutputDTO['brightness'] = {};
  if (data['brightness.bright'] !== undefined) brightness.bright = data['brightness.bright'];
  if (data['brightness.dark'] !== undefined) brightness.dark = data['brightness.dark'];

  const grainsize: SoilClassificationOutputDTO['grainsize'] = {};
  if (data['grainSize.sand'] !== undefined) grainsize.sand = data['grainSize.sand'];
  if (data['grainSize.finerThanSand'] !== undefined) grainsize.finer = data['grainSize.finerThanSand'];

  const gravel: SoilClassificationOutputDTO['gravel'] = {};
  const hasGravel =
    data['gravelShare.0pct'] !== undefined ||
    data['gravelShare.10pct'] !== undefined ||
    data['gravelShare.20pct'] !== undefined ||
    data['gravelShare.30pct'] !== undefined ||
    data['gravelShare.40pct'] !== undefined ||
    data['gravelShare.50pct'] !== undefined;
  if (hasGravel) {
    gravel.low = Math.max(data['gravelShare.0pct'] ?? 0, data['gravelShare.10pct'] ?? 0);
    gravel.medium = Math.max(data['gravelShare.20pct'] ?? 0, data['gravelShare.30pct'] ?? 0);
    gravel.high = Math.max(data['gravelShare.40pct'] ?? 0, data['gravelShare.50pct'] ?? 0);
  }

  const mixed_waste: SoilClassificationOutputDTO['mixed_waste'] = {};
  const hasMixedWaste =
    data['mixedWasteShare.0pct'] !== undefined ||
    data['mixedWasteShare.10pct'] !== undefined ||
    data['mixedWasteShare.20pct'] !== undefined ||
    data['mixedWasteShare.30pct'] !== undefined ||
    data['mixedWasteShare.40pct'] !== undefined ||
    data['mixedWasteShare.50pct'] !== undefined;
  if (hasMixedWaste) {
    mixed_waste.low = Math.max(data['mixedWasteShare.0pct'] ?? 0, data['mixedWasteShare.10pct'] ?? 0);
    mixed_waste.medium = Math.max(data['mixedWasteShare.20pct'] ?? 0, data['mixedWasteShare.30pct'] ?? 0);
    mixed_waste.high = Math.max(data['mixedWasteShare.40pct'] ?? 0, data['mixedWasteShare.50pct'] ?? 0);
  }

  const wetProb = data.wet ?? 0;
  const wet: SoilClassificationOutputDTO['wet'] = { yes: wetProb, no: 1 - wetProb };

  const cohesiveProb = data.cohesive ?? 0;
  const sticky: SoilClassificationOutputDTO['sticky'] = { yes: cohesiveProb, no: 1 - cohesiveProb };

  const bioProb = data.biomaterials ?? 0;
  const biomaterials: SoilClassificationOutputDTO['biomaterials'] = { yes: bioProb, no: 1 - bioProb };

  return transformSoilClassification({
    color: colors,
    grainsize,
    gravel,
    mixed_waste,
    wet,
    biomaterials,
    plant_materials: {},
    brightness,
    sticky,
  });
}
